import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ArrowDown } from 'lucide-react';
import KinosLeadModal from "./KinosLeadModal";

const scrollToFeatures = () => {
  const featuresSection = document.getElementById('features');
  if (featuresSection) {
    featuresSection.scrollIntoView({ behavior: 'smooth' });
  }
};

const HeroSection = () => {
  const [stat1Count, setStat1Count] = useState(0);
  const [stat2Count, setStat2Count] = useState(0);
  const [stat3Count, setStat3Count] = useState(0);
  const [animationStarted, setAnimationStarted] = useState(false);

  const statsRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [activeTileIndex, setActiveTileIndex] = useState(0);

  const coachingTypes = [
    'Fitness Training',
    'Nutrition Coaching',
    'Habit Coaching',
    'Wellness Expert'
  ];

  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopIndex, setLoopIndex] = useState(0);
  const typingSpeed = 100;
  const pauseTime = 2000;

  const [email, setEmail] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const currentText = coachingTypes[loopIndex % coachingTypes.length];
    let typingInterval;

    if (isDeleting) {
      typingInterval = setInterval(() => {
        setDisplayedText((prev) => {
          const updated = currentText.substring(0, prev.length - 1);
          if (updated === '') {
            clearInterval(typingInterval);
            setIsDeleting(false);
            setLoopIndex((prevIndex) => prevIndex + 1);
          }
          return updated;
        });
      }, typingSpeed / 2);
    } else {
      typingInterval = setInterval(() => {
        setDisplayedText((prev) => {
          const updated = currentText.substring(0, prev.length + 1);
          if (updated === currentText) {
            clearInterval(typingInterval);
            setTimeout(() => setIsDeleting(true), pauseTime);
          }
          return updated;
        });
      }, typingSpeed);
    }

    return () => clearInterval(typingInterval);
  }, [isDeleting, loopIndex, coachingTypes]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !animationStarted) {
          setAnimationStarted(true);
          startCountAnimation();
        }
      },
      { threshold: 0.1 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) observer.unobserve(statsRef.current);
    };
  }, [animationStarted]);

  const startCountAnimation = () => {
    let count1 = 0;
    const interval1 = setInterval(() => {
      count1 += 1;
      setStat1Count(count1);
      if (count1 >= 10) clearInterval(interval1);
    }, 100);

    let count2 = 0;
    const interval2 = setInterval(() => {
      count2 += 1;
      setStat2Count(count2);
      if (count2 >= 25) clearInterval(interval2);
    }, 40);

    let count3 = 0;
    const interval3 = setInterval(() => {
      count3 += 500;
      setStat3Count(count3);
      if (count3 >= 10000) clearInterval(interval3);
    }, 50);
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollPosition = container.scrollLeft;
      const tileWidth = container.scrollWidth / 3;
      const newIndex = Math.round(scrollPosition / tileWidth);
      setActiveTileIndex(newIndex);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <section className="relative bg-white overflow-hidden text-gray-800">
      <div className="absolute inset-0 opacity-5 bg-pattern"></div>
      {/* <div className="max-w-6xl mx-auto px-4 relative z-10 pt-16 md:pt-24 pb-5 md:pb-32"> */}
      <div className="max-w-6xl mx-auto px-4 relative z-12 pt-4 md:pt-24 pb-2 md:pb-4">

        <div className="text-center mb-6 md:mb-10">
          <div className="inline-flex items-center mb-4 md:mb-6 bg-[#c4ffa0]/20 px-3 py-1 md:px-5 md:py-2 rounded-full text-xs md:text-sm">
            <span className="mr-1 md:mr-2.5">✨</span>
            <span className="text-[#2d4a1e] font-medium">Trusted by growth-minded coaches</span>
            <span className="ml-1 md:ml-2.5">✨</span>
          </div>

          <div className="relative min-h-[100px] sm:min-h-[120px] flex items-center justify-center mb-4 md:mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight px-2 py-1 relative z-10 text-gray-800">
              Triple Your{' '}
              <span className="relative inline-block min-w-[180px] md:min-w-[280px] text-left">
                <span className="text-[#4a7a2e] cursor-pointer">{displayedText}</span>
                <span className="inline-block w-0.5 h-6 md:h-8 bg-[#c4ffa0] ml-1 animate-blink"></span>
              </span>{' '}
              Revenue with kinOS
            </h1>
          </div>

          <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed px-2">
          KinOS is the AI business platform for coaches, freelancers, and creators who want to grow faster with less effort.
            {/* The AI-powered platform that scales your coaching business with personalized plans, automation, and new income streams. Focus on what you do best—coaching your clients to success. */}
          </p>

          <form
            className="flex flex-col sm:flex-row gap-3 w-full max-w-md mx-auto mt-6"
            onSubmit={e => {
              e.preventDefault();
              setModalOpen(true);
            }}
          >
            <input
              type="email"
              required
              placeholder="Enter your email"
              // className="flex-1 rounded-lg border border-[#c4ffa0] bg-[#c4ffa0]/10 px-4 py-3 text-[#2d4a1e] text-sm"
              className="flex-1 rounded-lg border border-[#c4ffa0] bg-[#c4ffa0]/10 px-4 py-3 text-[#2d4a1e] text-base"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <button
              type="submit"
               className="px-5 py-3 bg-[#c4ffa0] text-[#2d4a1e] text-sm md:text-base font-semibold rounded-lg hover:shadow-lg transition duration-300 hover:bg-[#b3ee90] flex justify-center items-center"

            
            >
              Get Started
            </button>
          </form>

          {/* <div className="flex justify-center mt-4">
            <button
              onClick={scrollToFeatures}
              className="w-full sm:w-auto bg-white text-gray-700 border border-gray-200 px-6 md:px-6 py-3 md:py-3 text-sm md:text-base rounded-lg hover:bg-gray-50 transition duration-300 font-medium flex items-center justify-center gap-2"
            >
              <span>See How It Works</span>
              <ArrowDown size={16} className="text-[#2d4a1e]" />
            </button>
          </div> */}

          <KinosLeadModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            email={email}
          />

          <div className="mt-6 text-xs md:text-sm text-gray-500">
            No credit card required • Cancel anytime
          </div>
        </div>
      </div>

   

      <style jsx>{`
        .bg-pattern {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
        
        .stat-number {
          transition: all 0.5s ease-out;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        .animate-blink {
          animation: blink 1s step-start infinite;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;